import React, { createContext, useContext, useState, useEffect } from 'react';
import { calculateShiftTimings } from 'utils/shiftTimingCalculator';
import { useAuth } from './AuthContext';

const EmployeeContext = createContext();

export const useEmployees = () => {
  const context = useContext(EmployeeContext);
  if (!context) {
    throw new Error('useEmployees must be used within an EmployeeProvider');
  }
  return context;
};

export const EmployeeProvider = ({ children }) => {
  const [employees, setEmployees] = useState([]);
  const { user, isAuthenticated } = useAuth();

  // Load employees from localStorage or backend when authentication state changes
  useEffect(() => {
    async function initializeEmployees() {
      console.log('EmployeeProvider: Initializing...', { isAuthenticated, user });
      
      // Only proceed if user is authenticated
      if (!isAuthenticated || !user) {
        console.log('EmployeeProvider: User not authenticated, skipping initialization');
        setEmployees([]);
        return;
      }
      
      const savedEmployees = localStorage.getItem('employees');
      console.log('EmployeeProvider: Saved employees:', savedEmployees);
      const token = localStorage.getItem('token');
      if (savedEmployees) {
        try {
          const parsed = JSON.parse(savedEmployees);
          console.log('EmployeeProvider: Parsed employees:', parsed);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setEmployees(parsed);
            return;
          } else {
            throw new Error('Invalid employees data');
          }
        } catch (error) {
          console.error('EmployeeProvider: Error parsing saved employees:', error);
          localStorage.removeItem('employees');
        }
      }
      // If no valid localStorage, fetch from backend only if token exists
      if (token) {
        try {
          const { employees } = await import('../api/cacheAfterLogin').then((mod) => mod.cacheAfterLogin());
          console.log('EmployeeProvider: Employees loaded from backend:', employees);
          setEmployees(employees);
        } catch (err) {
          console.error('EmployeeProvider: Error loading employees from backend:', err);
          setEmployees([]);
        }
      } else {
        console.warn('EmployeeProvider: No token found, cannot fetch from backend');
        setEmployees([]);
      }
    }
    initializeEmployees();
  }, [isAuthenticated, user]);


  // Save employees to localStorage whenever they change
  useEffect(() => {
    if (employees.length > 0) {
      console.log('EmployeeProvider: Saving employees to localStorage:', employees);
      localStorage.setItem('employees', JSON.stringify(employees));
    }
  }, [employees]);

  const addEmployee = (newEmployee) => {
    const employeeWithId = {
      ...newEmployee,
      id: Date.now() // Simple ID generation
    };
    setEmployees((prev) => [...prev, employeeWithId]);
  };

  const removeEmployee = (index) => {
    setEmployees((prev) => prev.filter((_, i) => i !== index));
  };

  const updateEmployee = (index, updatedEmployee) => {
    setEmployees((prev) => prev.map((emp, i) => (i === index ? updatedEmployee : emp)));
  };

  const getEmployeesBySubstore = (substoreId) => {
    return employees.filter((emp) => emp.substoreId === substoreId);
  };

  const getEmployeesWithShiftInfo = (substoreId, substores) => {
    try {
      const substore = substores[substoreId];
      if (!substore) return [];

      const employeesInSubstore = getEmployeesBySubstore(substoreId);

      // Calculate shift timings for this substore
      const shiftTimings = calculateShiftTimings(substore.workingTimeStart, substore.workingTimeEnd, parseInt(substore.numShifts));

      // If no shift timings calculated, return employees without shift info
      if (!shiftTimings || shiftTimings.length === 0) {
        return employeesInSubstore;
      }

      // Assign employees to shifts based on their ID
      return employeesInSubstore.map((employee, index) => {
        const shiftIndex = index % shiftTimings.length;
        const assignedShift = shiftTimings[shiftIndex] || shiftTimings[0];

        return {
          ...employee,
          shiftInfo: {
            shiftId: assignedShift.id,
            shiftName: assignedShift.name,
            startTime: assignedShift.startTime,
            endTime: assignedShift.endTime,
            duration: assignedShift.duration
          }
        };
      });
    } catch (error) {
      console.error('Error calculating shift info:', error);
      // Return employees without shift info if there's an error
      return getEmployeesBySubstore(substoreId);
    }
  };

  const value = {
    employees,
    addEmployee,
    removeEmployee,
    updateEmployee,
    getEmployeesBySubstore,
    getEmployeesWithShiftInfo
  };

  console.log('EmployeeProvider: Current employees:', employees);

  return <EmployeeContext.Provider value={value}>{children}</EmployeeContext.Provider>;
};
